import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { Button, IconButton } from './button.js';
import { cx } from './cx.js';
/** How long a toast stays: longer with an action, so there is time to take it. */
const SHOW_MS = 5_000;
const SHOW_WITH_ACTION_MS = 9_000;
/** At most this many at once; the oldest goes first. */
const MAX = 3;
const ToastContext = createContext(null);
/**
 * Says what just happened (after Corly): saved, removed - with a way back. Without a
 * provider the call does nothing instead of failing, so a component works anywhere.
 */
export function useToast() {
    return useContext(ToastContext) ?? noop;
}
const noop = () => undefined;
const TONE = {
    info: { icon: Info, className: 'text-info-fg' },
    success: { icon: CircleCheck, className: 'text-success-fg' },
    error: { icon: CircleAlert, className: 'text-danger-fg' },
};
function ToastItem({ toast, onClose }) {
    const [paused, setPaused] = useState(false);
    const left = useRef(toast.action ? SHOW_WITH_ACTION_MS : SHOW_MS);
    // A pointer over it or the focus in it stops the clock (WCAG 2.2.1): reading takes its time.
    useEffect(() => {
        if (paused)
            return;
        const started = Date.now();
        const timer = setTimeout(onClose, left.current);
        return () => {
            clearTimeout(timer);
            left.current -= Date.now() - started;
        };
    }, [paused, onClose]);
    const tone = TONE[toast.tone ?? 'info'];
    const Icon = tone.icon;
    return (_jsxs("li", { 
        // An error interrupts; anything else waits for a pause in what is read.
        role: toast.tone === 'error' ? 'alert' : 'status', onPointerEnter: () => setPaused(true), onPointerLeave: () => setPaused(false), onFocus: () => setPaused(true), onBlur: () => setPaused(false), className: "glass flex items-start gap-3 rounded-lg bg-surface/95 p-4 shadow-lg motion-safe:animate-[fade-in_150ms_ease-out]", children: [_jsx(Icon, { "aria-hidden": true, className: cx('mt-0.5 size-5 shrink-0', tone.className) }), _jsxs("div", { className: "grid min-w-0 flex-1 gap-1", children: [_jsx("p", { className: "font-semibold", children: toast.title }), toast.description && _jsx("p", { className: "text-sm text-muted", children: toast.description }), toast.action && (_jsx("div", { children: _jsx(Button, { variant: "secondary", size: "sm", onPress: () => {
                                toast.action?.onAction();
                                onClose();
                            }, children: toast.action.label }) }))] }), _jsx(IconButton, { "aria-label": "Meldung schlie\u00DFen", size: "sm", className: "-mt-1 -mr-1", onPress: onClose, children: _jsx(X, { "aria-hidden": true, className: "size-4" }) })] }));
}
/** The place of the toasts: bottom left on a desk, across the bottom on a phone. */
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const next = useRef(0);
    const show = useCallback((toast) => {
        next.current += 1;
        const id = next.current;
        setToasts((all) => [...all, { ...toast, id }].slice(-MAX));
    }, []);
    const close = useCallback((id) => {
        setToasts((all) => all.filter((t) => t.id !== id));
    }, []);
    const value = useMemo(() => show, [show]);
    return (_jsxs(ToastContext.Provider, { value: value, children: [children, _jsx("section", { "aria-label": "Meldungen", 
                // On a phone above the bar of places (`--app-foot`, theme.css), never over it.
                className: "pointer-events-none fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom),calc(var(--app-foot)+0.5rem))] z-50 sm:right-auto sm:left-6 sm:w-96", children: _jsx("ol", { className: "pointer-events-auto grid gap-2", children: toasts.map((toast) => (_jsx(ToastItem, { toast: toast, onClose: () => close(toast.id) }, toast.id))) }) })] }));
}
//# sourceMappingURL=toast.js.map